import { useQuery, useQueryClient } from "@tanstack/react-query";
import { ArrowRight, Check, Crown, FolderPlus, ListTree, Loader2 } from "lucide-react";
import { useState } from "react";

import { api, background } from "../../api/client";
import type { ChainInfo, ChainSubordinate, Exercise, Level } from "../../api/types";
import { Dialog } from "../../components/Dialog";
import { EmptyState } from "../../components/EmptyState";
import { Button } from "../../components/ui";
import { k, t, tServer } from "../../i18n/i18n";
import { useUi } from "../../state/ui";
import { zoneRefusalOf } from "../../map/zones/zoneRefusal";
import { accountsItem, isOwner, useSharing } from "../../sharing/access";
import { declinable } from "../declination/declination";

/** Transmission de l'OPO aux subordonnés (enchaînement des ordres) : pour chaque subordonné d'une division
 *  (brigades), d'une brigade (GTIA) ou d'un GTIA (S/GTIA), « Créer son dossier » ouvre, dans cette licence, le dossier de son propre
 *  ordre — même terrain, ennemi, date, fuseau horaire et météo, son ordre de bataille — avec l'OPO rangé dans ses
 *  « Ordres reçus », à analyser et reporter dans ses Données initiales. Ensuite, le dossier d'un S/GTIA suit la date, le
 *  fuseau horaire et la météo du GTIA (sauf ceux qu'il a choisis) ; celui d'un autre niveau en est prévenu et reporte ce
 *  qui convient depuis l'ordre reçu, météo comprise (chantier de cohérence météo du 30/10/2026). Un subordonné qui a sa
 *  propre licence reçoit ce dossier par un code à usage unique (registre A67), affiché une seule fois ; ses codes en
 *  attente s'annulent, sa réception s'affiche. */

const LEVEL_NAME: Partial<Record<Level, string>> = {
  DIVISION: k("division"),
  BRIGADE: k("brigade"),
  REGIMENT: k("GTIA"),
  COMPAGNIE: k("S/GTIA"),
};

/** Niveau d'un subordonné, traduit ; un niveau sans nom reste tel quel. */
function levelName(level: Level): string {
  const name = LEVEL_NAME[level];
  return name ? t(name) : level;
}

export function TransmitDialog({ exerciseId, number, onClose }: { exerciseId: string; number: number; onClose: () => void }) {
  const queryClient = useQueryClient();
  const notify = useUi((s) => s.notify);
  const setExercise = useUi((s) => s.setExercise);
  const setView = useUi((s) => s.setView);
  const key = ["chain", exerciseId] as const;
  // Transmission par code vers une autre licence retirée (demande d'Alexis du 08/10/2026) : seule la création du
  // dossier du subordonné dans cette licence reste.
  const query = useQuery({ queryKey: key, queryFn: () => background(() => api.chain(exerciseId)), staleTime: 0 });
  // Action en cours : « ref:dossier ».
  const [busy, setBusy] = useState<string | null>(null);
  const data = query.data;
  const open = (id: string, mapSlug: string | null) => {
    onClose();
    setExercise(id, mapSlug);
    setView("received");
  };
  const create = async (ref: string) => {
    setBusy(`${ref}:dossier`);
    try {
      const next = await api.transmitOrder(exerciseId, ref, number);
      queryClient.setQueryData<ChainInfo>(key, next);
      void queryClient.invalidateQueries({ queryKey: ["exercises"] });
      // Avis du serveur (météo reçue ramenée au terrain du dossier créé…), en français : traduits à l'affichage.
      const notices = next.notices ?? [];
      notify({
        text: t("Dossier créé : {name}", { name: next.dossier.name }),
        detail: [t("L'OPO est rangé dans ses ordres reçus, prêt à être analysé et reporté."), ...notices.map((n) => tServer(n))].join(" "),
        tone: "ok",
        action: { label: t("Ouvrir"), run: () => open(next.dossier.id, next.dossier.map_slug) },
      });
    } catch (e) {
      // Ligne de la zone tenue un instant par le service (écriture finale) : « Réessayer » rejoue la création.
      if (zoneRefusalOf(e) === "ZONE_BUSY")
        notify({
          text: t("Transmission impossible"),
          detail: (e as Error).message,
          tone: "info",
          action: { label: t("Réessayer"), run: () => void create(ref) },
        });
      else notify({ text: t("Transmission impossible"), detail: (e as Error).message, tone: "error" });
    } finally {
      setBusy(null);
    }
  };
  // Liste des dossiers suivie (et non lue une fois) : un dossier tout juste créé y arrive à sa relecture.
  const { data: exercises = [] } = useQuery<Exercise[]>({ queryKey: ["exercises"], queryFn: api.exercises });
  const slugOf = (id: string) => exercises.find((e) => e.id === id)?.map_slug ?? null;
  // Comptes nominatifs (docs/17 §3.5) : le dossier créé appartient au compte qui transmet ; son propriétaire peut le
  // transférer au compte du chef subordonné (« Transférer la propriété… » de la fenêtre « Partager »).
  const transferable = (id: string | null | undefined) => {
    const ex = id ? exercises.find((e) => e.id === id) : undefined;
    return Boolean(ex && accountsItem(ex) && isOwner(ex));
  };
  const proposeOwner = (id: string) => {
    const ex = exercises.find((e) => e.id === id);
    if (!ex) return;
    onClose();
    useSharing.getState().open(ex);
  };

  return (
    <Dialog
      title={t("Transmettre l'OPO n° {n}", { n: number })}
      subtitle={t("Chaque subordonné reçoit le dossier de son propre ordre, dans cette licence.")}
      width={600}
      onClose={onClose}
      footer={
        <Button variant="secondary" onClick={onClose}>
          {t("Fermer")}
        </Button>
      }
    >
      {!data ? (
        <EmptyState variant="loading" compact title={t("Recherche des subordonnés…")} />
      ) : !data.next_level ? (
        <p className="px-4 py-5 text-ui leading-relaxed text-ink-soft">
          {t(
            "L'OPO d'un S/GTIA s'adresse à ses sections ou à ses pelotons, qui n'ont pas de dossier propre dans Generex Web : la transmission se fait depuis un dossier de GTIA, de brigade, de division ou de corps d'armée.",
          )}
        </p>
      ) : data.subordinates.length === 0 ? (
        <p className="px-4 py-5 text-ui text-ink-muted">{t("Aucun subordonné trouvé dans l'ordre de bataille du dossier.")}</p>
      ) : (
        <>
          {declinable(data.level) && (
            // Brigade ou division : tous les ordres subordonnés rédigés d'un geste, réunis dans un document (docs/20 §13).
            <div className="flex flex-wrap items-center gap-x-3 gap-y-2 border-b border-line bg-accent-soft px-4 py-3">
              <p className="min-w-[220px] flex-1 text-ui leading-relaxed text-ink">
                {data.level === "CORPS"
                  ? t("Ou générez d'un geste les ordres des divisions et de leurs brigades : chacun reçoit son dossier, son fuseau et son OPO rédigé, réunis dans un seul document.")
                  : data.level === "DIVISION"
                  ? t("Ou générez d'un geste les ordres des brigades et de leurs GTIA : chacun reçoit son dossier, son fuseau et son OPO rédigé, réunis dans un seul document.")
                  : data.level === "REGIMENT"
                  ? t("Ou générez d'un geste les ordres de tous les S/GTIA : chacun reçoit son dossier, sa zone et son OPO rédigé, réunis dans un seul document.")
                  : t("Ou générez d'un geste les ordres de tous les GTIA : chacun reçoit son dossier, son fuseau et son OPO rédigé, réunis dans un seul document.")}
              </p>
              <Button
                size="sm"
                variant="secondary"
                onClick={() => {
                  onClose();
                  setView("declination");
                }}
              >
                <ListTree size={14} /> {t("Ordres subordonnés")}
              </Button>
            </div>
          )}
          {/* « Fuseau horaire » : le fuseau seul est la zone d'action de chaque subordonné (bandeau ci-dessus). Un S/GTIA suit
              ensuite la date, le fuseau horaire et la météo du GTIA ; un autre niveau en est prévenu et reporte ce qui
              convient depuis l'ordre reçu. */}
          <p className="border-b border-line px-4 py-3 text-ui leading-relaxed text-ink-soft">
            {data.next_level === "COMPAGNIE"
              ? t(
                  "Même terrain, même ennemi, même début d'exercice, même fuseau horaire et même météo ; l'ordre de bataille du S/GTIA. Son OPO reprend ensuite celui du GTIA : si vous changez la date, le fuseau horaire ou la météo, son dossier les suit, sauf s'il a choisi les siens.",
                )
              : t(
                  "Même terrain, même ennemi, même début d'exercice, même fuseau horaire et même météo ; l'ordre de bataille du subordonné. Rien n'est reporté d'office ensuite : prévenu des changements de votre ordre, son état-major analyse l'ordre reçu puis reporte ce qui convient, mission ou météo.",
                )}
          </p>
          <ul className="divide-y divide-line">
            {data.subordinates.map((sub) => (
              <SubordinateRow
                key={sub.ref ?? sub.label}
                sub={sub}
                busy={busy}
                onOpen={() => open(sub.dossier!.id!, slugOf(sub.dossier!.id!))}
                onCreate={() => void create(sub.ref!)}
                onTransfer={transferable(sub.dossier?.id) ? () => proposeOwner(sub.dossier!.id!) : undefined}
              />
            ))}
          </ul>
        </>
      )}
    </Dialog>
  );
}

function SubordinateRow({
  sub,
  busy,
  onOpen,
  onCreate,
  onTransfer,
}: {
  sub: ChainSubordinate;
  busy: string | null;
  onOpen: () => void;
  onCreate: () => void;
  /** Dossier créé dont le compte est propriétaire : « Transférer la propriété… » au chef subordonné. */
  onTransfer?: () => void;
}) {
  return (
    <li className="px-4 py-2.5">
      <div className="flex flex-wrap items-center gap-x-3 gap-y-2">
        <div className="min-w-[180px] flex-1">
          <p className="text-ui font-semibold text-ink">{sub.label}</p>
          <p className="truncate text-meta text-ink-muted">
            {sub.dossier?.hidden
              ? t("Dossier créé, non partagé avec vous")
              : sub.dossier
                ? t("Dossier « {name} »", { name: sub.dossier.name ?? "" })
              : sub.reason
                ? t("Pas de dossier possible : {reason}", { reason: tServer(sub.reason) })
                : t("Dossier de {level} à créer", { level: levelName(sub.level) })}
          </p>
          {/* Ordres transmis prévenus : l'ordre a changé depuis sa transmission, le dossier le signale à son
              ouverture (rien n'y est reporté d'office). */}
          {sub.dossier?.notice && <p className="text-meta text-accent">{t("Prévenu : l'ordre a changé depuis sa transmission")}</p>}
          {onTransfer && (
            <Button variant="link" onClick={onTransfer}>
              <Crown size={13} /> {t("Transférer la propriété…")}
            </Button>
          )}
        </div>
        {sub.ref && (
          <div className="flex shrink-0 items-center gap-1.5">
            {sub.dossier?.hidden ? null : sub.dossier ? (
              <Button size="sm" variant="secondary" onClick={onOpen}>
                <Check size={14} className="text-ok" /> {t("Ouvrir")} <ArrowRight size={14} />
              </Button>
            ) : (
              <Button size="sm" disabled={busy !== null} onClick={onCreate}>
                {busy === `${sub.ref}:dossier` ? <Loader2 size={14} className="animate-spin" /> : <FolderPlus size={14} />}{" "}
                {t("Créer son dossier")}
              </Button>
            )}
          </div>
        )}
      </div>
    </li>
  );
}
